import type { ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { NavLink as MantineNavLink, Stack, Text, UnstyledButton } from '@mantine/core';
import {
  IconApps,
  IconBuildingEstate,
  IconChartBar,
  IconChevronLeft,
  IconChevronRight,
  IconChevronUp,
  IconClipboardList,
  IconFolders,
  IconMail,
  IconLayoutDashboard,
  IconLifebuoy,
  IconMessage2,
  IconSettings,
  IconCreditCard,
  IconShieldLock,
  IconReceipt,
  IconTicket,
  IconUsersGroup,
} from '@tabler/icons-react';
import { Box } from '@xhr-labs/core-ui';
import { ROUTES } from '@/lib/routes';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

interface NavItem {
  path: string;
  label: string;
  icon: ReactNode;
  children?: NavItem[];
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    label: 'Core',
    items: [
      { path: ROUTES.DASHBOARD, label: 'Dashboard', icon: <IconLayoutDashboard size={18} /> },
      { path: ROUTES.USERS, label: 'Users', icon: <IconUsersGroup size={18} /> },
      { path: ROUTES.COMPANIES, label: 'Companies', icon: <IconBuildingEstate size={18} /> },
      { path: ROUTES.AUDIT, label: 'Audit', icon: <IconClipboardList size={18} /> },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        path: ROUTES.SUPPORT,
        label: 'Support',
        icon: <IconLifebuoy size={18} />,
        children: [
          { path: ROUTES.SUPPORT_TICKETS, label: 'Tickets', icon: <IconTicket size={16} /> },
          { path: ROUTES.SUPPORT_CATEGORIES, label: 'Categories', icon: <IconFolders size={16} /> },
          { path: ROUTES.SUPPORT_QUEUES, label: 'Queues', icon: <IconMessage2 size={16} /> },
          { path: ROUTES.SUPPORT_REPORTS, label: 'Reports', icon: <IconChartBar size={16} /> },
          {
            path: ROUTES.SUPPORT_GOD_ADMIN,
            label: 'God Admin',
            icon: <IconShieldLock size={16} />,
          },
        ],
      },
      { path: ROUTES.APPSTORE, label: 'App Store', icon: <IconApps size={18} /> },
      {
        path: ROUTES.SUBSCRIPTIONS,
        label: 'Subscription Management',
        icon: <IconCreditCard size={18} />,
        children: [
          {
            path: ROUTES.SUBSCRIPTION_COMPANIES,
            label: 'Companies',
            icon: <IconBuildingEstate size={16} />,
          },
          { path: ROUTES.SUBSCRIPTION_PLANS, label: 'Plans', icon: <IconCreditCard size={16} /> },
          {
            path: ROUTES.SUBSCRIPTION_CUSTOM_PRODUCTS,
            label: 'Custom products',
            icon: <IconReceipt size={16} />,
          },
        ],
      },
      { path: ROUTES.CRM_PRODUCT_UPDATES, label: 'Product updates', icon: <IconMail size={18} /> },
      { path: ROUTES.SETTINGS, label: 'Settings', icon: <IconSettings size={18} /> },
    ],
  },
];

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const getNavLinkStyles = (active: boolean, centered: boolean) => ({
    root: {
      color: active
        ? 'var(--xhr-color-content-primary-strong)'
        : 'var(--xhr-color-content-neutral-medium)',
      border: active ? '1px solid var(--xhr-color-border-primary-light)' : '1px solid transparent',
      backgroundColor: active ? 'var(--xhr-color-bg-fill-primary-subtle)' : 'transparent',
      borderRadius: 'var(--xhr-corner-radius-sm)',
      minHeight: 44,
      marginInline: 'var(--xhr-pad-sm)',
      paddingInline: centered ? 'var(--xhr-pad-sm)' : 'var(--xhr-pad-md)',
      justifyContent: centered ? 'center' : 'flex-start',
      boxShadow: active
        ? '0 10px 20px color-mix(in srgb, var(--xhr-color-bg-fill-primary) 8%, transparent)'
        : 'none',
    },
    label: {
      color: 'inherit',
      fontWeight: active ? 600 : 500,
    },
  });

  return (
    <Box
      h="100%"
      style={{
        background:
          'linear-gradient(180deg, color-mix(in srgb, var(--xhr-color-bg-surface) 98%, transparent) 0%, color-mix(in srgb, var(--v2-color-neutral-5) 94%, var(--xhr-color-bg-surface)) 100%)',
        borderRight: '1px solid var(--xhr-color-border-neutral-light)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        style={{
          padding: 'var(--xhr-pad-md)',
          borderBottom: '1px solid var(--xhr-color-border-neutral-light)',
        }}
      >
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: 'var(--xhr-pad-sm)',
          }}
        >
          <Box
            style={{
              width: 34,
              height: 34,
              borderRadius: 'var(--xhr-corner-radius-sm)',
              background:
                'linear-gradient(135deg, var(--xhr-color-bg-fill-primary) 0%, var(--v2-color-blue-70) 100%)',
              color: 'var(--xhr-color-content-inverse)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 700,
              boxShadow:
                '0 10px 24px color-mix(in srgb, var(--xhr-color-bg-fill-primary) 18%, transparent)',
            }}
          >
            AP
          </Box>
          {!collapsed ? (
            <Box style={{ minWidth: 0 }}>
              <Text
                fw={700}
                size="sm"
                c="var(--xhr-color-content-neutral-strong)"
                style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}
              >
                Admin Portal
              </Text>
              <Text size="xs" c="dimmed" mt={2}>
                Platform operations
              </Text>
            </Box>
          ) : null}
        </Box>
        {!collapsed ? (
          <Box
            style={{
              marginTop: 'var(--xhr-pad-md)',
              paddingInline: 'var(--xhr-pad-sm)',
              paddingBlock: '6px',
              borderRadius: 'var(--xhr-corner-radius-sm)',
              backgroundColor: 'var(--xhr-color-bg-fill-primary-subtle)',
            }}
          >
            <Text size="xs" fw={700} c="var(--xhr-color-content-primary-strong)">
              Live workspace
            </Text>
          </Box>
        ) : null}
      </Box>

      <Box
        style={{
          paddingInline: 'var(--xhr-pad-md)',
          paddingTop: 'var(--xhr-pad-md)',
        }}
      >
        {!collapsed ? (
          <Text size="xs" fw={600} c="dimmed">
            Navigation
          </Text>
        ) : null}
      </Box>

      <Stack gap="md" py="md" style={{ flex: 1, overflow: 'auto' }}>
        {navSections.map((section) => (
          <Stack key={section.label} gap={4}>
            {!collapsed ? (
              <Text
                size="xs"
                fw={700}
                tt="uppercase"
                c="dimmed"
                px="md"
                style={{ letterSpacing: 0.8 }}
              >
                {section.label}
              </Text>
            ) : null}
            {section.items.map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              const firstChild = item.children !== undefined ? item.children[0] : undefined;
              const firstChildPath = firstChild !== undefined ? firstChild.path : undefined;
              const isActive = hasChildren
                ? location.pathname.startsWith(item.path)
                : location.pathname === item.path;

              if (item.children && item.children.length > 0 && !collapsed) {
                const children = item.children;
                return (
                  <Box
                    key={item.path}
                    style={{
                      marginInline: 'var(--xhr-pad-sm)',
                    }}
                  >
                    <UnstyledButton
                      onClick={() => {
                        void navigate(firstChildPath ?? item.path);
                      }}
                      aria-label={item.label}
                      style={{
                        width: '100%',
                        color: isActive
                          ? 'var(--xhr-color-content-primary-strong)'
                          : 'var(--xhr-color-content-neutral-medium)',
                        border: isActive
                          ? '1px solid var(--xhr-color-border-primary-light)'
                          : '1px solid transparent',
                        backgroundColor: isActive
                          ? 'var(--xhr-color-bg-fill-primary-subtle)'
                          : 'transparent',
                        borderRadius: 'var(--xhr-corner-radius-sm)',
                        minHeight: 44,
                        padding: '10px var(--xhr-pad-md)',
                        boxShadow: isActive
                          ? '0 10px 20px color-mix(in srgb, var(--xhr-color-bg-fill-primary) 8%, transparent)'
                          : 'none',
                      }}
                    >
                      <Box
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 'var(--xhr-pad-sm)',
                        }}
                      >
                        <Box
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--xhr-pad-sm)',
                            minWidth: 0,
                          }}
                        >
                          <Text
                            size="lg"
                            style={{ width: 20, textAlign: 'center', display: 'flex' }}
                          >
                            {item.icon}
                          </Text>
                          <Text fw={isActive ? 600 : 500} c="inherit">
                            {item.label}
                          </Text>
                        </Box>
                        <Box aria-hidden="true" style={{ display: 'flex', alignItems: 'center' }}>
                          {location.pathname.startsWith(item.path) ? (
                            <IconChevronUp size={16} />
                          ) : (
                            <IconChevronRight size={16} />
                          )}
                        </Box>
                      </Box>
                    </UnstyledButton>
                    <Stack gap={4} mt={4} pl="md">
                      {children.map((child) => {
                        const childActive = location.pathname === child.path;
                        return (
                          <MantineNavLink
                            key={child.path}
                            component={NavLink}
                            to={child.path}
                            label={child.label}
                            leftSection={
                              <Text
                                size="lg"
                                style={{ width: 20, textAlign: 'center', display: 'flex' }}
                              >
                                {child.icon}
                              </Text>
                            }
                            active={childActive}
                            variant="subtle"
                            styles={getNavLinkStyles(childActive, false)}
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                );
              }

              if (item.children && item.children.length > 0 && collapsed) {
                return (
                  <MantineNavLink
                    key={item.path}
                    component={NavLink}
                    to={firstChildPath ?? item.path}
                    leftSection={
                      <Text size="lg" style={{ width: 20, textAlign: 'center', display: 'flex' }}>
                        {item.icon}
                      </Text>
                    }
                    active={isActive}
                    variant="subtle"
                    styles={getNavLinkStyles(isActive, true)}
                    title={item.label}
                  />
                );
              }

              return (
                <MantineNavLink
                  key={item.path}
                  component={NavLink}
                  to={item.path}
                  label={collapsed ? undefined : item.label}
                  leftSection={
                    <Text size="lg" style={{ width: 20, textAlign: 'center', display: 'flex' }}>
                      {item.icon}
                    </Text>
                  }
                  active={isActive}
                  variant="subtle"
                  styles={getNavLinkStyles(isActive, collapsed)}
                  title={collapsed ? item.label : undefined}
                />
              );
            })}
          </Stack>
        ))}
      </Stack>

      <UnstyledButton
        onClick={onToggle}
        p="md"
        w="100%"
        style={{
          borderTop: '1px solid var(--xhr-color-border-neutral-light)',
          color: 'var(--xhr-color-content-neutral-medium)',
          transition: 'color 0.2s',
        }}
        aria-label="Toggle sidebar"
      >
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'space-between',
            gap: 'var(--xhr-pad-sm)',
          }}
        >
          {!collapsed ? (
            <Text size="sm" fw={500}>
              Collapse sidebar
            </Text>
          ) : null}
          <Box
            aria-hidden="true"
            style={{
              width: 28,
              height: 28,
              borderRadius: '999px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'var(--xhr-color-bg-fill-neutral-subtle)',
            }}
          >
            {collapsed ? <IconChevronRight size={16} /> : <IconChevronLeft size={16} />}
          </Box>
        </Box>
      </UnstyledButton>
    </Box>
  );
}
